<template>
	<template v-if="companyData && Object.keys(companyData).length > 0">
		<!-- Meta tags -->
		<Title>{{ companyData.name }} - DreamJobs</Title>
		<Link v-if="globalConfig.isBlue" rel="canonical" :href="'https://dreamjobs.hu' + $route.fullPath" />
		<Link v-else rel="canonical" :href="'https://' + globalConfig.host + $route.fullPath" />
		<Link v-if="globalConfig.isBlue" rel="icon" type="image/x-icon" href="/blue-favicon.ico?v3" />
		<Link v-else rel="icon" type="image/x-icon" href="/favicon.ico?v3" />
		<Meta property="og:url" :content="'https://' + globalConfig.host + $route.fullPath" />
		<Meta property="og:type" content="article" />
		<Meta
			property="og:locale"
			:content="`${$i18n.locale}_${$i18n.locale == 'en' ? 'US' : $i18n.locale.toUpperCase()}`"
		/>
		<Meta property="og:title" :content="companyData.name + ' - ' + 'DreamJobs'" />
		<Meta
			property="og:description"
			:content="
				companyData.status && companyData?.boxes?.mission?.data?.mission?.[locale]
					? removeHtmlTagFromString(companyData.boxes.mission.data.mission[locale])
					: companyData.name
			"
		/>
		<Meta
			v-if="companyData?.boxes?.team_photo?.data?.length"
			property="og:image"
			:content="`https://res.cloudinary.com/djbs/image/upload/${companyData.boxes.team_photo.data[0].cloudinary}.jpg`"
		/>
		<Meta
			v-if="companyData?.boxes?.team_photo?.data?.length"
			property="og:image:secure_url"
			:content="`https://res.cloudinary.com/djbs/image/upload/${companyData.boxes.team_photo.data[0].cloudinary}.jpg`"
		/>
		<Meta v-if="companyData?.boxes?.team_photo?.data?.length" property="og:image:type" content="image/jpeg" />
		<Meta
			v-if="companyData?.boxes?.team_photo?.data?.length"
			property="og:image:alt"
			:content="companyData.name + ' - ' + 'DreamJobs'"
		/>
		<Meta
			v-if="companyData?.boxes?.team_photo?.data?.length"
			property="og:image:height"
			:content="imageSize.height.toString()"
		/>
		<Meta
			v-if="companyData?.boxes?.team_photo?.data?.length"
			property="og:image:width"
			:content="imageSize.width.toString()"
		/>
	</template>
	<section class="flex flex-col bg-[#F9FBFE]">
		<div
			v-if="companyData && hasBoxes && companyData?.boxes?.team_photo?.data?.length"
			class="flex items-center max-w-[1480px] md:max-w-[calc(100%_-_80px)] lg:max-w-[1480px] mx-auto w-full h-auto md:min-h-[435px] min-h-[150px] bg-dj-gray md:rounded-bl-[40px] md:rounded-br-[40px] overflow-hidden"
		>
			<BaseCloudImage
				v-if="hasBoxes && companyData?.boxes?.team_photo?.data?.length"
				custom-class="w-full h-auto hidden"
				:public-id="companyData.boxes.team_photo.data[0].cloudinary"
				:transformation="[{ width: 1480, crop: 'scale' }]"
				postfix=".jpg"
				:alt="companyData.name"
				@getUrl="imageUrl = $event"
				@getSize="imageSize = $event"
			/>
			<ImageSlider v-if="images && images.length > 0" autoSlides :images="images" />
		</div>
		<div class="container min-h-[640px]">
			<CompanyProfileHeader
				v-if="companyData && companyData.status"
				:logo="companyData.logo?.cloudinary ?? ''"
				:name="companyData.name"
				:status="companyData.status"
				:mission="
					companyData.status &&
					hasBoxes &&
					companyData.boxes.mission &&
					companyData.boxes.mission.data.mission
						? companyData.boxes.mission.data.mission[locale]
						: null
				"
				:motto="
					companyData.status && hasBoxes && companyData.boxes.mission
						? companyData.boxes.mission.data.motto[locale]
						: null
				"
				:location="
					companyData.status && hasBoxes && companyData.boxes.address
						? companyData.boxes.address.data[0].formatted_address
						: null
				"
				:tags="companyTags"
				:jobs="companyData.jobs.length"
				:company-id="companyData.id"
				:company-slug="companyData.slug"
				:company-badges="companyData.badges.length > 0 ? companyData.badges : null"
				:szmd-status="companyData.szmd_status"
				:szmd="companyData.szmd"
				class="w-full"
			/>
			<BannerContainer banner-type="frontend-company-profile-banner" class="banner-section" v-slot="{ content }">
				<section
					class="container relative flex flex-col md:flex-row justify-center mx-5 sm:mx-8 md:mx-auto my-8"
					v-html="content"
				></section>
			</BannerContainer>
			<CompanyProfileBoxes
				v-if="companyData && companyData.status && hasBoxes"
				:company-data="companyData"
				:locale="locale"
				:has-sharingBox="true"
			/>
		</div>

		<SectionsBlog
			v-if="companyData && companyData.blog_posts && companyData.blog_posts.length != 0"
			:blog-data="companyData.blog_posts"
			:blog-title="$t('blogposts')"
		/>

		<JobsPageCarousel
			v-if="companyData && companyData.jobs.length != 0"
			id="company-profile-carousel"
			:title="$t('company.openJobs', { company: companyData.name })"
			:raw-jobs-data="companyData.jobs"
			hide-all-jobs
			carouselType=""
		/>
		<template v-if="!simplePage">
			<template v-if="isAuth">
				<JobsPageCarousel
					:title="$t('jobs.recommended')"
					:carousel-data-url="recommendedCarouselUrl"
					:data-url="recommendedDataUrl"
					hasGradientBg
					route-param="allRecommendedJobs"
					@data-fetched="processApiData"
					sortByGold
					carouselType="offered-jobs"
				/>
			</template>
			<template v-else>
				<JobsPageCarousel
					:title="$t('jobs.highlighted')"
					highlighted-gold
					hasGrafientBg
					route-param="highlightedJobs"
					@data-fetched="processApiData"
					carouselType="higlighted-company-jobs"
				/>
			</template>
		</template>
		<client-only>
			<BaseModal
				v-model="showFollowCompanyModal"
				:title="'&nbsp;'"
				:otherModalClasses="followCompanyModalClasses"
			>
				<CompanyProfileFollowCompanySection
					:location="locale"
					:companySlug="companyData.slug"
					:companyId="companyData.id"
					:withImage="true"
				></CompanyProfileFollowCompanySection>
			</BaseModal>
		</client-only>
	</section>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, inject } from "vue";
import axios from "axios";
import { useI18n } from "vue-i18n";
import { removeHtmlTagFromString } from "~~/utils";
import { axiosErrorHandler } from "@@/composables/helpers";
import { useCompanySchema } from "@@/composables/microdata/useCompanySchema";

export default defineComponent({
	name: "CompanyProfilePage",
	async setup() {
		const route = useRoute();
		const companyData = ref();
		const companyTags = ref([]);

		const images = ref<string[]>([]);

		const recommendedCarouselUrl = ref("jobs?");
		const recommendedDataUrl = ref(globalConfig.API);
		const isBlue = ref(globalConfig.isBlue);
		const isNotBlueHU = ref(globalConfig.isNotBlueHU);

		// For OG meta tags
		const imageUrl = ref("");
		const imageSize = ref({ width: 0, height: 0 });

		const { locale } = useI18n();

		const isAuth = useState("isAuth");

		const showFollowCompanyModal = ref(false);
		const followCompanyModalClasses = ref("follow-company-modal");
		const isClosedFCModal = useCookie<number>("isClosedFCModal");
		const simplePage = inject("simplePage", ref(false));

		onMounted(() => {
			window.scrollTo(0, 0);
			isAuth.value == true && buildRecommendedUrl();
			window.addEventListener("scroll", handleScroll);
		});
		onUnmounted(() => {
			// Remove scroll event listener

			window.removeEventListener("scroll", handleScroll);
		});

		await axios
			.get(
				recommendedDataUrl.value +
					`/companies/${route.params.slug}` +
					(isBlue.value ? "?scope[]=isBlue" : isNotBlueHU.value ? "?scope[]=isNotBlue" : ""),
				{
					headers: {
						"Cache-Control": "no-cache",
						Pragma: "no-cache",
					},
				}
			)
			.then((response) => {
				companyData.value = response.data;
				if (Array.isArray(response.data?.boxes?.team_photo?.data)) {
					images.value = response.data.boxes.team_photo.data.map((img) => {
						return useCloudinary().url(img.cloudinary + ".jpg");
					});
				}
				mergeTags();
			})
			.catch((error) => {
				axiosErrorHandler("CompanyProfilePage", error);
				// go to 404 page if company not found
				companyData.value = null;
				if (error.response?.status == 404) {
					showError(
						createError({
							statusCode: 404,
							statusMessage: "Company not found",
						})
					);
				}
			});

		function handleScroll() {
			const scrollPosition = window.scrollY;
			const scrollWindowHeight = window.innerHeight;

			if (
				scrollPosition > scrollWindowHeight &&
				isAuth.value == true &&
				!useAuth.isLovedCompany(companyData.value.id)
			) {
				if (!isClosedFCModal.value) {
					showFollowCompanyModal.value = true;
					isClosedFCModal.value = true;
				}
			}
		}

		function mergeTags() {
			const neededTags = ["industry", "location", "company_size"];
			neededTags.forEach((tag) => {
				if (companyData.value.tags[tag] != undefined) {
					if (tag == "location") {
						//companyTags.value.push(companyData.value.tags[tag].data[0].name);
						companyData.value.tags[tag].data.map((tag) => {
							companyTags.value.push(tag.name);
						});
					} else {
						companyTags.value.push(companyData.value.tags[tag].name);
					}
				}
				[];
			});
		}

		function buildRecommendedUrl() {
			// region
			recommendedCarouselUrl.value += `region=${useRegion.getCurrentRegion().code.toLowerCase()}`;
			// categories
			if (useAuth.getUser().tags.job_category) {
				useAuth.getUser().tags.job_category.data.forEach((cat) => {
					recommendedCarouselUrl.value += `&tags[job-category][]=${cat.id}`;
				});
			}
			// subcategories
			if (useAuth.getUser().tags.job_subcategory) {
				useAuth.getUser().tags.job_subcategory.data.forEach((cat, index) => {
					if (index == 0 && recommendedCarouselUrl.value.match(new RegExp("tags", "gi")) == null) {
						recommendedCarouselUrl.value += `tags[job-subcategory][]=${cat.id}`;
					} else {
						recommendedCarouselUrl.value += `&tags[job-subcategory][]=${cat.id}`;
					}
				});
			}
			// job levels
			if (useAuth.getUser().tags.job_level) {
				useAuth.getUser().tags.job_level.data.forEach((level, index) => {
					if (index == 0 && recommendedCarouselUrl.value.match(new RegExp("tags", "gi")) == null) {
						recommendedCarouselUrl.value += `tags[job-level][]=${level.id}`;
					} else {
						recommendedCarouselUrl.value += `&tags[job-level][]=${level.id}`;
					}
				});
			}

			if (globalConfig.isBlue) {
				recommendedCarouselUrl.value += "&scope[]=isBlue";
			}
			if (globalConfig.isNotBlueHU) {
				recommendedCarouselUrl.value += "&scope[]=isNotBlue";
			}

			recommendedDataUrl.value += `/${recommendedCarouselUrl}`;
		}

		function processApiData(event) {
			// apiUrlFromCarousel.value = event.url
			// searchingFromCarousel.value = true
			const router = useRouter();
			const query = {};
			query[event.param] = "true";
			router.push({
				path: `/${locale.value}/jobs`,
				query: query,
			});
		}

		const hasBoxes = computed(() => {
			return companyData.value?.boxes && Object.keys(companyData.value.boxes).length > 0;
		});

		useCompanySchema(companyData, locale);

		return {
			isAuth,
			simplePage,
			companyData,
			companyTags,
			locale,
			recommendedCarouselUrl,
			recommendedDataUrl,
			processApiData,
			hasBoxes,
			imageUrl,
			imageSize,
			removeHtmlTagFromString,
			images,
			showFollowCompanyModal,
			followCompanyModalClasses,
		};
	},
});
</script>
<style>
.follow-company-modal {
	@apply bg-white max-w-[500px] min-w-[300px];
	header {
		@apply mb-[-10px];
	}
	button {
		@apply w-[300px] mx-auto mb-5;
	}
	.title {
		@apply font-bold text-[28px];
	}
	.description {
		@apply font-medium text-[22px];
		line-height: inherit;
	}
}
</style>
